import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { ICON_MAP } from 'src/utils/constants';
import { GeneralASError } from 'src/registry/types/axios';
import { cn } from 'src/utils';
import { ServicePlanTerm } from 'src/features/servicePlanTerms/utils/types';
import {
    servicePlanTermFormSchema,
    ServicePlanTermFormSchemaValues,
} from 'src/features/servicePlanTerms/utils/formSchema';
import ServicePlanTermForm from 'src/features/servicePlanTerms/components/forms/ServicePlanTermForm';
import { useGetServicePlanTerm } from 'src/features/servicePlanTerms/api/useGetServicePlanTerm';
import { useParams } from 'react-router-dom';
import { getDirtyValues } from 'src/utils/getDirtyValues';

interface ServicePlanTermFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedServicePlanTerm?: ServicePlanTerm;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: ServicePlanTermFormSchemaValues,
        onSuccess: () => void,
        onError: (error: GeneralASError) => void,
    ) => void;
}

function ServicePlanTermFormModal<TData>({
    selectedServicePlanTerm,
    isPending,
    children,
    className,
    icon,
    title,
    subtitle,
    mutate,
}: ServicePlanTermFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { servicePlanId = '' } = useParams();

    const { data: servicePlanTerm } = useGetServicePlanTerm({
        servicePlanId: servicePlanId ?? '',
        planTermId: selectedServicePlanTerm?.id ?? '',
        config: { enabled: !!selectedServicePlanTerm?.id && isModalOpen },
    });
    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        return {
            valid_from: servicePlanTerm?.valid_from || undefined,
            currency: servicePlanTerm?.currency || undefined,
            valid_to: servicePlanTerm?.valid_to || undefined,
            trial_period_days: servicePlanTerm?.trial_period_days || undefined,
            unpaid_grace_period_days: servicePlanTerm?.unpaid_grace_period_days || undefined,
            lock_warning_days: servicePlanTerm?.lock_warning_days || undefined,
            lock_grace_period_days: servicePlanTerm?.lock_grace_period_days || undefined,
            stop_warning_days: servicePlanTerm?.stop_warning_days || undefined,
            stop_grace_period_days: servicePlanTerm?.stop_grace_period_days || undefined,
        };
    }, [servicePlanTerm]);

    const form = useForm<ServicePlanTermFormSchemaValues>({
        resolver: zodResolver(servicePlanTermFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, isModalOpen, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: ServicePlanTermFormSchemaValues) => {
            const dirtyFields = getDirtyValues(form.formState.dirtyFields, values);
            mutate(
                { ...dirtyFields } as ServicePlanTermFormSchemaValues,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },
        [form.formState.dirtyFields, form.setError, mutate, onError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <ServicePlanTermForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedServicePlanTerm && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ServicePlanTermFormModal;
